import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toaster from '../../utils/toaster';
import { confirmPendingLink, fetchPendingLink } from '../service';
import {
    GENERIC_ERROR_KEY,
    INVITE_ERROR_KEYS,
    LINK_TOKEN_PARAM,
    SLACK_AGENT_EVENTS,
    TOAST_AUTO_CLOSE_MS,
    toastKey,
    trackSlackAgentEvent,
} from '../constants';

// The invite the bot posts in Slack for an unlinked member: the link carries a one-time token.
const useSlackLinkInvite = (translate, onLinked) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [pendingLink, setPendingLink] = useState(null);
    const [busy, setBusy] = useState(false);

    const showFailure = useCallback(
        key => toaster.error(translate(toastKey(key)), { autoClose: TOAST_AUTO_CLOSE_MS }),
        [translate]
    );

    const consumedTokenRef = useRef(null);

    useEffect(() => {
        const linkToken = searchParams.get(LINK_TOKEN_PARAM);
        if (!linkToken || consumedTokenRef.current === linkToken) return;
        consumedTokenRef.current = linkToken;
        // Cleared before anything async: the token is single use, so a reload must not retry it.
        setSearchParams(
            previous => {
                const next = new URLSearchParams(previous);
                next.delete(LINK_TOKEN_PARAM);
                return next;
            },
            { replace: true }
        );

        fetchPendingLink(linkToken)
            .then(invite => {
                setPendingLink(invite);
                trackSlackAgentEvent(SLACK_AGENT_EVENTS.INVITE_OPENED);
            })
            .catch(error => showFailure(INVITE_ERROR_KEYS[error.status] ?? GENERIC_ERROR_KEY));
    }, [searchParams, setSearchParams, showFailure]);

    const acceptLink = async () => {
        setBusy(true);
        try {
            const linked = await confirmPendingLink(pendingLink.confirm_token);
            setPendingLink(null);
            trackSlackAgentEvent(SLACK_AGENT_EVENTS.ACCOUNT_LINKED);
            onLinked();
            // Telling someone to go and ask again reads as a failure when the answer is already coming.
            toaster.success(
                translate(toastKey(linked?.answering_pending_question ? 'ACCOUNT_LINKED_ANSWERING' : 'ACCOUNT_LINKED')),
                { autoClose: TOAST_AUTO_CLOSE_MS }
            );
        } catch (error) {
            setPendingLink(null);
            showFailure(INVITE_ERROR_KEYS[error.status] ?? GENERIC_ERROR_KEY);
        } finally {
            setBusy(false);
        }
    };

    const rejectLink = () => setPendingLink(null);

    return { pendingLink, busy, acceptLink, rejectLink };
};

export default useSlackLinkInvite;
